import { Controller } from '@hotwired/stimulus'
import { stimulus } from '~/init'

export default class DrawerController extends Controller {
  static values = {
    swipeThreshold: { type: Number, default: 100 }
  }

  get #isMobile () { return window.innerWidth < 640 }

  connect () {
    this._onBackdropClick = this.#closeOnBackdropClick.bind(this)
    this.element.addEventListener('click', this._onBackdropClick)

    if (this.#isMobile) {
      this._onTouchStart = this.#lockTouchStart.bind(this)
      this._onTouchMove = this.#lockOverscroll.bind(this)
      this.element.addEventListener('touchstart', this._onTouchStart, { passive: true })
      this.element.addEventListener('touchmove', this._onTouchMove, { passive: false })
    } else {
      this._onTouchStart = this.#onSwipeStart.bind(this)
      this._onTouchMove = this.#onSwipeMove.bind(this)
      this._onTouchEnd = this.#onSwipeEnd.bind(this)
      this.element.addEventListener('touchstart', this._onTouchStart, { passive: true })
      this.element.addEventListener('touchmove', this._onTouchMove, { passive: false })
      this.element.addEventListener('touchend', this._onTouchEnd, { passive: true })
    }

    this.element.showModal()

    this._onBeforeRender = this.#closeBeforeRender.bind(this)
    document.addEventListener('turbo:before-render', this._onBeforeRender)
  }

  disconnect () {
    document.removeEventListener('turbo:before-render', this._onBeforeRender)
    this.element.removeEventListener('click', this._onBackdropClick)
    if (this._onTouchStart) this.element.removeEventListener('touchstart', this._onTouchStart)
    if (this._onTouchMove) this.element.removeEventListener('touchmove', this._onTouchMove)
    if (this._onTouchEnd) this.element.removeEventListener('touchend', this._onTouchEnd)
    this.close()
  }

  show () {
    this.element.showModal()
  }

  close () {
    try { this.element.close() } catch (e) {}
    try {
      const frame = DrawerController.turboFrame
      if (frame) frame.src = null
    } catch (e) {}
    try { this.element.remove() } catch (e) {}
  }

  #closeBeforeRender () {
    this.close()
  }

  #closeOnBackdropClick (event) {
    if (event.target === this.element) {
      this.close()
    }
  }

  // ── Mobile: prevent dialog overscroll ──

  #lockTouchStart (event) {
    this._lockStartX = event.touches[0].clientX
    this._lockStartY = event.touches[0].clientY
  }

  #lockOverscroll (event) {
    const dx = event.touches[0].clientX - this._lockStartX
    const dy = event.touches[0].clientY - this._lockStartY

    // Block horizontal movement
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 5) {
      event.preventDefault()
      return
    }

    // Find the nearest scroll container (by CSS, not by content size)
    const scroller = this.#findScrollContainer(event.target)

    // No scroll container — block everything (nothing should scroll)
    if (!scroller) {
      event.preventDefault()
      return
    }

    const { scrollTop, scrollHeight, clientHeight } = scroller
    const canScroll = scrollHeight > clientHeight

    // Content fits without scroll — block (no reason to move anything)
    if (!canScroll) {
      event.preventDefault()
      return
    }

    // Content overflows — allow scroll, block only at boundaries
    const atTop = scrollTop <= 0 && dy > 0
    const atBottom = scrollTop + clientHeight >= scrollHeight && dy < 0

    if (atTop || atBottom) {
      event.preventDefault()
    }
  }

  #findScrollContainer (el) {
    while (el && el !== this.element) {
      const overflow = getComputedStyle(el).overflowY
      if (overflow === 'auto' || overflow === 'scroll') return el
      el = el.parentElement
    }
    return null
  }

  // ── Desktop/tablet: swipe-to-close ──

  #onSwipeStart (event) {
    this.touchStartX = event.touches[0].clientX
    this.touchStartY = event.touches[0].clientY
    this.swiping = null
  }

  #onSwipeMove (event) {
    const currentX = event.touches[0].clientX
    const currentY = event.touches[0].clientY
    const diffX = currentX - this.touchStartX
    const diffY = currentY - this.touchStartY

    if (this.swiping === null) {
      if (Math.abs(diffX) < 10 && Math.abs(diffY) < 10) return
      this.swiping = Math.abs(diffX) > Math.abs(diffY)
      if (this.swiping) {
        this.element.style.transition = 'none'
      }
    }

    if (!this.swiping) return

    event.preventDefault()
    const offset = Math.max(0, diffX)
    this.element.style.transform = `translateX(${offset}px)`
  }

  #onSwipeEnd (event) {
    if (!this.swiping) {
      this.swiping = null
      return
    }

    const touchEndX = event.changedTouches[0].clientX
    const diff = touchEndX - this.touchStartX

    this.element.style.transition = 'transform 0.2s ease-out'

    if (diff > this.swipeThresholdValue) {
      this.element.style.transform = 'translateX(100%)'
      this.element.addEventListener('transitionend', () => this.close(), { once: true })
    } else {
      this.element.style.transform = ''
      this.element.style.transition = ''
    }

    this.swiping = null
  }

  static get turboFrame () {
    return document.querySelector('turbo-frame[id=\'drawer\']')
  }
}

stimulus.register('drawer', DrawerController)
